import {
  type UseQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type AnnounceBannerItem,
  fetchAnnounceBanners,
} from "@/features/announce/api/announceClient";
import {
  getAnnouncementRetryDelay,
  shouldRetryAnnouncementRequest,
} from "@/features/announce/api/announcementRetryPolicy";
import { getLastGoodQueryData } from "@/features/announce/hooks/announcementLastGood";
import {
  ANNOUNCEMENT_META_POLL_INTERVAL_MS,
  createAnnouncementMetaQueryOptions,
} from "@/features/announce/hooks/announcementMetaQuery";

export type AnnouncementBannerRevision = number | "unknown";

interface AnnouncementBannerQuery {
  state: {
    status: "pending" | "error" | "success";
    error: Error | null;
  };
}

interface AnnouncementBannerQueryOptions {
  queryKey: readonly ["announce", "banner", AnnouncementBannerRevision];
  queryFn: (context: { signal: AbortSignal }) => Promise<AnnounceBannerItem[]>;
  staleTime: number;
  refetchInterval: (query: AnnouncementBannerQuery) => number | false;
  refetchIntervalInBackground: false;
  refetchOnWindowFocus: true;
  refetchOnReconnect: true;
  retry: typeof shouldRetryAnnouncementRequest;
  retryDelay: typeof getAnnouncementRetryDelay;
}

export function createAnnouncementBannerQueryOptions(
  revision: AnnouncementBannerRevision,
): AnnouncementBannerQueryOptions {
  return {
    queryKey: ["announce", "banner", revision] as const,
    queryFn: ({
      signal,
    }: {
      signal: AbortSignal;
    }): Promise<AnnounceBannerItem[]> =>
      fetchAnnounceBanners(
        signal,
        revision === "unknown" ? undefined : revision,
      ),
    staleTime: ANNOUNCEMENT_META_POLL_INTERVAL_MS,
    refetchInterval: (query: AnnouncementBannerQuery) =>
      query.state.status === "error" &&
      query.state.error !== null &&
      shouldRetryAnnouncementRequest(0, query.state.error)
        ? ANNOUNCEMENT_META_POLL_INTERVAL_MS
        : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: shouldRetryAnnouncementRequest,
    retryDelay: getAnnouncementRetryDelay,
  };
}

export function useAnnounceBanners(): UseQueryResult<AnnounceBannerItem[]> {
  const metaQuery = useQuery(createAnnouncementMetaQueryOptions());
  const queryClient = useQueryClient();
  const logicalQueryKey = ["announce", "banner"] as const;
  return useQuery({
    ...createAnnouncementBannerQueryOptions(
      metaQuery.data?.banner ?? "unknown",
    ),
    initialData: () =>
      getLastGoodQueryData<AnnounceBannerItem[]>(queryClient, logicalQueryKey),
    initialDataUpdatedAt: 0,
  });
}
